import { formatAddress, formatDate, formatPhone, titleCase } from '../BusinessDetailReport/businessReportModel'
import { moreFromLexis } from './moreFromLexis'

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const text = (value) => (value == null || typeof value === 'object' ? '' : String(value)).trim()
const dateValue = (date) => (date && typeof date === 'object' && date.Year ? new Date(date.Year, (date.Month || 1) - 1, date.Day || 1).getTime() : 0)
const records = (section) => {
    const value = section && typeof section === 'object' ? Object.values(section).find(Array.isArray) : null
    return (value || []).filter(r => r && typeof r === 'object')
}
const personName = (n) => titleCase([n?.First, n?.Middle, n?.Last, n?.Suffix].filter(Boolean).join(' ') || n?.Full)
const range = (from, to) => [...new Set([formatDate(from), formatDate(to)].filter(Boolean))].join(' to ')
const yesNo = (value) => ({ Y: 'Yes', N: 'No' })[text(value).toUpperCase()] || titleCase(value)
const pairs = (rows) => rows.filter(([, value]) => value).map(([label, value]) => ({ label, value }))

export function buildOtherRecords(person) {
    const voters = records(person?.VoterRegistrations).map(v => ({
        at: dateValue(v.RegistrationDate),
        more: moreFromLexis(v, 'VoterRegistrations/VoterRegistration[]'),
        title: `${titleCase(v.RegistrateStateName) || v.RegistrateState || 'Voter'} registration`,
        facts: [titleCase(v.VoterStatus || v.ActiveOrInactive), v.RegistrationDate && `Registered ${formatDate(v.RegistrationDate)}`].filter(Boolean).join(' · '),
        chips: [],
        details: pairs([
            ['Status', [titleCase(v.VoterStatus), titleCase(v.ActiveOrInactive), ({ A: 'Active', I: 'Inactive' })[text(v.ActiveOrInactiveFlag).toUpperCase()]]
                .filter((x, i, all) => x && all.indexOf(x) === i).join(' · ')],
            ['Registered', formatDate(v.RegistrationDate)],
            ['Residence address', formatAddress(v.ResidentAddress)],
            ['Mailing address', formatAddress(v.MailingAddress)],
            ['Phones', [formatPhone(v.HomePhone) && `home ${formatPhone(v.HomePhone)}`, formatPhone(v.WorkPhone) && `work ${formatPhone(v.WorkPhone)}`].filter(Boolean).join(', ')],
            ['Occupation', titleCase(v.Occupation)],
            ['Age range', [text(v.AgeRange), titleCase(v.AgeCategory)].filter(Boolean).join(' · ')],
            ['Name on record', personName(v.Name)],
            ['Maiden name', titleCase(v.MaidenName)],
            ['Date of birth on record', formatDate(v.DOB)],
        ]),
    })).sort((a, b) => b.at - a.at)

    const students = records(person?.PossibleStudentRecords).map(s => {
        const college = s.CollegeData || {}
        const study = s.StudentData || {}
        return {
            at: dateValue(s.LastReported),
            more: moreFromLexis(s, 'PossibleStudentRecords/PossibleStudentRecord[]'),
            title: titleCase(college.Name || college.OriginalName) || (s.HighSchoolGradYear ? `High school class of ${s.HighSchoolGradYear}` : 'Possible student record'),
            facts: [titleCase(college.Major), titleCase(college.Level), titleCase(college.Type), range(s.FirstReported, s.LastReported) && `Reported ${range(s.FirstReported, s.LastReported)}`]
                .filter(Boolean).join(' · '),
            chips: [s.IsCurrent === true && { label: 'Current', tone: 'neutral' }].filter(Boolean),
            details: pairs([
                ['College', [titleCase(college.Name), college.OriginalName && titleCase(college.OriginalName) !== titleCase(college.Name) && titleCase(college.OriginalName)].filter(Boolean).join(' · ')],
                ['Major and level', [titleCase(college.Major), titleCase(college.Level)].filter(Boolean).join(' · ')],
                ['School', [titleCase(college.Type), titleCase(study.CollegePublicPrivate1), text(study.College2Yr4YrGrad1) && `${text(study.College2Yr4YrGrad1)}-year`,
                    titleCase(s.SchoolSize) && `size ${titleCase(s.SchoolSize)}`, text(s.Tuition) && `tuition ${titleCase(s.Tuition)}`].filter(Boolean).join(' · ')],
                ['Program tier', text(study.CollegeEducationProgramTier1)],
                ['Second college', [yesNo(study.AttendedCollege2) && `attended: ${yesNo(study.AttendedCollege2).toLowerCase()}`, titleCase(study.CollegePublicPrivate2),
                    text(study.College2Yr4YrGrad2) && `${text(study.College2Yr4YrGrad2)}-year`, text(study.CollegeEducationProgramTier2) && `tier ${text(study.CollegeEducationProgramTier2)}`]
                    .filter(Boolean).join(' · ')],
                ['Class rank', titleCase(s.ClassRank)],
                ['School period', titleCase(s.SchoolPeriod)],
                ['High school', [yesNo(study.AttendedHighSchool) && `attended: ${yesNo(study.AttendedHighSchool).toLowerCase()}`,
                    s.HighSchoolGradYear && `graduated ${s.HighSchoolGradYear}`, text(study.YearsSinceHSGraduation) && `${text(study.YearsSinceHSGraduation)} years ago`]
                    .filter(Boolean).join(' · ')],
                ['Attended college', yesNo(study.AttendedCollege1)],
                ['Address at college', formatAddress(s.AddressAtCollege)],
                ['Phone at college', formatPhone(s.PhoneAtCollege)],
                ['Reported', range(s.FirstReported, s.LastReported)],
                ['Name on record', personName(s.Name)],
                ['Date of birth on record', formatDate(s.DOB)],
            ]),
        }
    }).sort((a, b) => b.at - a.at)

    const utilities = records(person?.UtilitiesData).map(u => {
        const addresses = list(u.Addresses?.Address).filter(Boolean)
        return {
            at: dateValue(u.ConnectDate) || dateValue(u.DateFirstSeen),
            more: moreFromLexis(u, 'UtilitiesData/UtilityData[]'),
            title: titleCase(u.UtilityTypeDescription || u.UtilityType || u.UtilityCategory) || 'Utility connection',
            facts: [u.ConnectDate && `Connected ${formatDate(u.ConnectDate)}`, formatAddress(addresses[0])].filter(Boolean).join(' · '),
            chips: [],
            details: pairs([
                ['Connected', formatDate(u.ConnectDate)],
                ['Category', [titleCase(u.UtilityCategory), text(u.UtilityType).length > 1 && titleCase(u.UtilityType)].filter(Boolean).join(' · ')],
                ['Addresses', addresses.map(a => [formatAddress(a), titleCase(a.Type) && `(${titleCase(a.Type).toLowerCase()})`].filter(Boolean).join(' ')).join('; ')],
                ['Phones', [formatPhone(u.Phone), formatPhone(u.WorkPhone) && `work ${formatPhone(u.WorkPhone)}`].filter(Boolean).join(', ')],
                ['First seen', formatDate(u.DateFirstSeen)],
                ['Record date', formatDate(u.RecordDate)],
                ['Name on record', personName(u.Name)],
            ]),
        }
    }).sort((a, b) => b.at - a.at)

    const hunting = records(person?.HuntingFishingLicenses).map(h => ({
        at: dateValue(h.LicenseDate),
        more: moreFromLexis(h, 'HuntingFishingLicenses/HuntingFishingLicense[]'),
        title: titleCase(h.LicenseType) || 'Hunting or fishing license',
        facts: [h.LicenseState, h.LicenseDate && `Issued ${formatDate(h.LicenseDate)}`].filter(Boolean).join(' · '),
        chips: [],
        details: pairs([
            ['License number', text(h.LicenseNumber)],
            ['Home state', text(h.HomeState)],
            ['Address on record', formatAddress(h.Address)],
            ['Name on record', personName(h.Name)],
            ['Date of birth on record', formatDate(h.DOB)],
        ]),
    })).sort((a, b) => b.at - a.at)

    const count = voters.length + students.length + utilities.length + hunting.length
    return { voters, students, utilities, hunting, count, hasData: count > 0 }
}
